import { NgxZendeskWebwidgetConfig } from '@nitsanzo/ngx-zendesk-webwidget';
import { UserInterests } from './user-interests';
import { AnalyticEvent } from './analytic-event';
import { ViewMode } from '../state/user/searches/models/search';

export interface ClientConfiguration {
  activeRecordsOrder?: number;
  allowSignUp: boolean;
  analyticEvents: AnalyticEvent[];
  assistant: Assistant;
  banners: Banner[];
  bannerSlideTime: number;
  bookClubName: string;
  bookClubUrl: string;
  consortia: boolean;
  consortiaMessage: string;
  country: string;
  customLoginUrl: string;
  customLogoutUrl: string;
  customSignUp: boolean;
  customSignUpUrl: string;
  enableDrmCaptcha: boolean;
  firstLoginMessage: string;
  firstLoginType: FirstMessageType;
  geolocation: boolean;
  googleAnalytics: string;
  googleTagManagerId: string;
  heapAppId: string;
  helpUrl: string;
  homeBanner: HomeBanner;
  homeFilter: HomeFilter;
  infoButtons: InfoButton[];
  languages: string[];
  leisureMode: boolean;
  locale: string;
  logErrors: boolean;
  loginMessage: string;
  loginOptions: LoginOption[];
  logoText: string;
  logoUrl: string;
  malfunctionBannerMessage: string | null;
  multipleSignUp: boolean;
  openResourceOnBorrow: boolean;
  privateMode: boolean;
  registerOptions: RegisterOption[];
  registrationMessage: string;
  rememberPasswordMessage: string;
  renewCheckouts: boolean;
  showAcsmButton: boolean;
  showAvailabilityFilter: boolean;
  showCheckoutHistory: boolean;
  showCookiesDisclaimer: boolean;
  showCopies: boolean;
  showDesiderata: boolean;
  showDevelopmentInfo: boolean;
  showAccessibilityCookieLinks: boolean;
  showDeviceManagement: boolean;
  showInterestsForm: boolean;
  showFadedBanner: boolean;
  showForgotPassword: boolean;
  showDeleteComments: boolean;
  showLanding: boolean;
  showPurchaseSuggestions: boolean;
  showRecommendations: boolean;
  showRemoteError: boolean;
  showResourceTotalViews: boolean;
  showReturnLoanButton: boolean;
  showSocialIcons: boolean;
  showStatistics: boolean;
  showTotalCheckouts: boolean;
  showUserData: UserDataOptions;
  showUserHistory: boolean;
  showVisualizationHistory: boolean;
  showCareerPlanFeatures: boolean;
  showWelcome: boolean;
  showWikipediaLink: boolean;
  showBookClubButton: boolean;
  showZendesk: boolean;
  showChallengeFeatures: boolean;
  gmfConfig: GmfConfig;
  showGamificationFeatures: boolean; // Legacy, delete when all clients >= 5.1 -> Also delete configService.setGamificationLegacyFields()
  rankingScope: RankingScope; // Legacy, delete when all clients >= 5.1
  showRemoteLoginBanner: boolean;
  showSimplifiedLogin: boolean;
  customerType: CustomerType;
  solutionType: string;
  termsUrl: string;
  theme: string;
  title: string;
  titleSeparator: string;
  topBar: TopBar;
  tutors: boolean;
  tutorsCentres: string[];
  userInterests: UserInterests;
  useSessionStorage: boolean;
  validIp: boolean;
  version: CustomerVersion;
  codCliente: string;
  themata: Map<string, string>;
  themaFilter: HomeFilter;
  customLabels: CustomLabels;
  privatePolicy: string;
  showAvailableCopies: boolean;
  youtubeIntegration: boolean;
  requireChangePasswordAfterFirstLogin?: boolean;
  deviceControlViaWeb?: boolean;
  deviceLimitExceeded?: boolean;
  exploreExperiencesButtonLabel?: string;
  showExperiencesFeatures?: boolean;
  inviteGuestAccountActive?: boolean;
  showInviteGuestAccount?: boolean;
  maxNumberInvitations: number;
  urlApiGatewayQueryCep: string;
  showHolds: boolean;
  smartBannerAndroid: string;
  smartBannerIos: string;
  showSliderUserActions: boolean;
  showActionCreateExperience: boolean;
  canCreateExperiences: boolean;
  showBookclubMode: boolean;
  showReportIssueButton: boolean;
  enableLogoWithLocale: boolean;
  partnetLibraryUrl: string;
  partnetLibraryToken: string;
  enableBookClubButtonToSection: boolean;
  showBookClubSection: boolean;
  showEventsSection: boolean;
  showCareerPlanSection: boolean;
  showExpireDateCollectionItem: boolean;
  userGroupFilterType: UserGroupFilterType;
  showActiveRecordsCarousel: boolean;
  publicListCreationDisabled: boolean;
  selfPublishingUrl: string;
  show24SymbolsBanner: boolean;
  certificationEntityId: string;
  enableUserHistoryExport: boolean;
  showCareerPlanPositions: boolean;
  ssoType: SsoType | null;
  showGoToOleButton: boolean;
  showExploreExperienceButton: boolean;
  showPublicationDate: boolean;
  useHighContrast: boolean | null;
  requireChangeMail: boolean;
  resultsViewMode: ViewMode;
  resultsFilterOrder: string;
  showAuthorViewCarousel: boolean;
  showReplyComment: boolean;
  maxReplyCommentLength: number;
}

export interface RemoteLoginData {
  url: string;
}

export enum FirstMessageType {
  FIRSTLOGIN_MSG = 'FIRSTLOGIN_MSG',
  FIRSTLOGIN_TERMS_COND = 'FIRSTLOGIN_TERMS_COND',
  FIRSTLOGIN_MSG_AND_TERMS_COND = 'FIRSTLOGIN_MSG_AND_TERMS_COND',
}

export interface CustomerVersion {
  version: string;
  build: string;
}

export interface Banner {
  id: string;
  hostId: number;
  ordering: number;
  title: string;
  content: string;
  url: string;
  image: string;
  align: string;
  showInfo?: boolean;
  clickable?: boolean;
}

export interface InfoButton {
  orden: number;
  link: string;
  textButton: string;
  textContent: string;
}

export interface HomeFilter {
  name: string;
  label: string;
  firstLevel: HomeFilterLevel[];
  type: HomeFilterType;
}

export type HomeFilterType = 'SLIDER' | 'TAGS';

export interface HomeFilterLevel {
  label: string;
  criteria: string[];
  nextLevel: HomeFilterLevel[];
  parentLabels: string[];
}

export interface HomeBanner {
  show: boolean;
  imageUrl: string;
  targetUrl: string;
}

export interface TopBar {
  show: boolean;
  leftText: string;
  centerText: string;
  rightText: string;
}

export interface LoginOption {
  integration: string;
  library?: string;
  name?: string;
  forgotPasswordEnabled?: boolean;
  fields: string[];
}

export interface RegisterOption {
  library: string;
  name: string;
  fields: UserParameter[];
  multipleId?: number;
}

export interface UserParameter {
  tipoParametro: ParameterType;
  alta: boolean;
  modificacion: boolean;
  obligatorio: boolean;
  defaultValue: string;
  multipleId: number;
  valueMap?: ComplexSelectValue[];
  tipoParametroHijo?: ParameterType;
  tipoParametroPadre?: ParameterType;
}

export interface RegisterParameter {
  type: ParameterType;
  value: string;
}

export enum ParameterType {
  // 0
  IDENTIFICADOR_EXTERNO,
  DATOS_INCOMPLETOS,
  TIPO_LECTOR,
  CODIGO,
  NOMBRE,
  // 5
  FECHA_INICIO,
  FECHA_FIN,
  ACCESO_AREA_USUARIOS,
  ALIAS_AREA_USUARIOS,
  OBSERVACIONES,
  // 10
  FOTO,
  CONTRASENNA,
  PERFIL,
  NUMERO_MAXIMO_DIAS,
  NUMERO_MAXIMO_UNIDADES,
  // 15
  GRUPO,
  SEXO,
  FECHA_NACIMIENTO,
  PAIS_ORIGEN,
  BIBLIOTECA_REFERENCIA,
  // 20
  TIPO_DIRECCION,
  NOMBRE_CONTACTO,
  DIRECCION,
  CODIGO_POSTAL,
  POBLACION,
  // 25
  PROVINCIA,
  PAIS,
  TELEFONO,
  FAX,
  CORREO_ELECTRONICO,
  // 30
  ANNO_ACADEMICO,
  CENTRO,
  DEPARTAMENTO,
  CICLO_FORMATIVO,
  CURSO,
  // 35
  GRUPO_DEL_CURSO,
  USER_ACCESS_TOKEN,
  MAX_PARAM,
  VALUE_ALIAS,
  ATTACH_FILES,
  // 40
  LENDING_POLICY,
  BIBLIOMETRO,
  AEROPUERTO,
  SANTIAGO,
  EMPRESA,
  // 45
  PUESTO,
  VALID_DOMAINS,
  VALIDATE_NEW_USER,
  MALLBARRIOINDEPENDENCIA,
  PROVINCIA_MINISTERIO_COLOMBIA,
  // 50
  POBLACION_MINISTERIO_COLOMBIA,
  CENTRO_MINISTERIO_COLOMBIA,
  CURSO_MINISTERIO_COLOMBIA,
  SEXO_MINISTERIO_COLOMBIA,
  ROL,
  // 55
  REGION,
  ESCOLARIDAD,
  SITUACION_LABORAL,
  NACIONALIDAD,
  TIPO_INSTITUCION,
  // 60
  AFILIADO,
  CATEGORIA,
  INVALID_DOMAINS,
}

export enum IdentifierType {
  DNI,
  NIF,
  NIE,
  PASAPORTE,
  TARJETA_RESIDENCIA,
  MATRICULA,
  ASOCIADO,
  OTRO,
  SIN,
  IBERIA,
  EMAIL,
  IDENTITY_CARD,
  CITIZENSHIP_CARD,
  RUT,
  USER_ID,
  LAST_NAME,
  FULL_NAME,
  BARCODE,
  USER_OR_BARCODE,
  CARD_NUMBER,
  LAST_4_DIGITS_PHONE,
  PARTNER_NUMBER,
  EXTERNAL_URL,
}

export enum PasswordType {
  PASSWORD,
  PIN,
  AZTECA_SIGNATURE,
  LAST,
  AZTECA,
}

export enum UserDataOptions {
  DATA_PASSWORD,
  NONE,
  ALL,
  CONFIGURED,
  PASSWORD,
  ACCESS_TOKEN,
  HIDE,
}

export enum USER_OF {
  NONE = 0,
  BIBLIOMETRO,
  AIRPORT,
  SANTIAGO_LIBRARY,
  MALL_BARRIO,
}

export interface StorageAction {
  action: string;
  key: string;
  value: string;
}

export class ZendeskConfig extends NgxZendeskWebwidgetConfig {
  lazyLoad = true;
  accountUrl = 'odilotid.zendesk.com';

  callback(zE: any): any {}
}

export interface Assistant {
  active: boolean;
  awsUrl: string;
  awsApiKey: string;
}

export interface SelectValue {
  value: string;
  label: string;
}

export interface ComplexSelectValue {
  parentId: number;
  values: SelectValue[];
}

export interface CustomerType {
  dbValue: string;
  menuOrder: string[];
}

export interface SolutionType {
  dbValue: string;
}

export enum RankingScope {
  PLATFORM = 'platform',
  CENTER = 'center',
}

export interface CustomLabels {
  ranking: RankingLabels;
}

export interface RankingLabels {
  scoreLabel: string;
  menuTitle: string;
  moreInformation: Map<string, string>;
}

export interface GmfConfig {
  showGamificationSection: boolean;
  showRanking: boolean;
  showPoints: boolean;
  showBadges: boolean;
  rankingScope: RankingScope;
}

export interface RemoteOtkBanner {
  logoUrl: string;
  altText: string;
}

export interface RemoteOtkBanner {
  logoUrl: string;
  altText: string;
}

export enum UserGroupFilterType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

export enum SsoType {
  SAML2 = 'SAML2',
  OAUTH2 = 'OAUTH2',
  EZPROXY = 'EZPROXY',
  CAS = 'CAS',
  OTK = 'OTK',
  CUSTOM_SSO = 'CUSTOM_SSO',
  LEIASP = 'LEIASP',
}
